import React, { useEffect, useRef } from "react";
import { Heart, Users, Globe, Sparkles, Mail, MapPin, Phone } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { Link, localizeTo } from "../i18n/router";

const AboutPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const joinSectionRef = useRef(null);
  
  // Auto-scroll to join section if hash is present (after login redirect)
  useEffect(() => {
    if (location.hash === '#join-community' && joinSectionRef.current) {
      setTimeout(() => {
        joinSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 500); // Small delay to ensure page is fully loaded
    }
  }, [location.hash]);
  
  return (
    <div className="eunoia-page">
      <div className="eunoia-container">
        {/* Header */}
        <div className="eunoia-text-center eunoia-space-xl">
          <h1 className="eunoia-title-large mb-6">
            О нашем сообществе
          </h1>
          <p className="eunoia-body-large max-w-3xl mx-auto">
            EUNOIA объединяет искателей и практиков по всей Европе, идущих к духовному росту, 
            внутренней гармонии и более осознанной жизни
          </p>
        </div>

        {/* Mission Statement */}
        <div className="eunoia-surface p-12 mb-16">
          <div className="text-center">
            <div 
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-8"
              style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}
            >
              <Heart className="h-10 w-10" style={{color: 'var(--eunoia-accent)'}} />
            </div>
            
            <h2 className="eunoia-title-medium mb-6">
              Наша миссия
            </h2>
            
            <p className="eunoia-body-large max-w-4xl mx-auto">
              Создать пространство, где каждый может найти поддержку на пути духовного развития, 
              изучить древние и современные практики, встретить единомышленников и открыть 
              новые грани своего внутреннего потенциала. Мы верим, что духовность — это 
              не догма, а живой процесс познания себя и мира.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="eunoia-title-medium text-center mb-12">
            Наши ценности
          </h2>
          
          <div className="eunoia-grid-3">
            <div className="eunoia-surface-soft text-center group hover:transform hover:translateY-[-2px] transition-all duration-300 p-8">
              <div 
                className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 mx-auto group-hover:scale-110 transition-transform duration-300"
                style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}
              >
                <Users className="h-10 w-10" style={{color: 'var(--eunoia-accent)'}} />
              </div>
              <h3 className="eunoia-title-small mb-4">Сообщество</h3>
              <p className="eunoia-body">
                Мы создаем теплую и поддерживающую атмосферу, где каждый чувствует себя принятым 
                и может безопасно делиться своим опытом и вопросами.
              </p>
            </div>

            <div className="eunoia-surface-soft text-center group hover:transform hover:translateY-[-2px] transition-all duration-300 p-8">
              <div 
                className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 mx-auto group-hover:scale-110 transition-transform duration-300"
                style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}
              >
                <Sparkles className="h-10 w-10" style={{color: 'var(--eunoia-accent)'}} />
              </div>
              <h3 className="eunoia-title-small mb-4">Аутентичность</h3>
              <p className="eunoia-body">
                Мы ценим искренность и подлинность в практиках и отношениях. 
                Здесь можно быть собой без масок и ожиданий.
              </p>
            </div>

            <div className="eunoia-surface-soft text-center group hover:transform hover:translateY-[-2px] transition-all duration-300 p-8">
              <div 
                className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 mx-auto group-hover:scale-110 transition-transform duration-300"
                style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}
              >
                <Globe className="h-10 w-10" style={{color: 'var(--eunoia-accent)'}} />
              </div>
              <h3 className="eunoia-title-small mb-4">Открытость</h3>
              <p className="eunoia-body">
                Мы открыты к различным духовным традициям и практикам, создавая 
                пространство для диалога и взаимного обучения.
              </p>
            </div>
          </div>
        </div>

        {/* History */}
        <div className="mb-16">
          <Card className="bg-white/80 backdrop-blur-sm border-emerald-100/50">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Наша история</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-w-4xl mx-auto">
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Наше сообщество зародилось в 2019 году в Эстонии, когда небольшая группа 
                  русскоговорящих практиков начала встречаться для совместных медитаций. 
                  Постепенно круг расширился, и мы поняли, что многие люди в Европе 
                  ищут духовную общность на родном языке.
                </p>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Сегодня наше сообщество объединяет сотни людей из разных стран Европы. 
                  Мы проводим регулярные встречи, семинары, ретриты и церемонии, создавая 
                  мосты между традиционными духовными практиками и современной жизнью.
                </p>

                <p className="text-gray-600 leading-relaxed">
                  Наша цель — не только практиковать вместе, но и создавать пространство 
                  для глубокого общения, взаимной поддержки и совместного роста. Мы верим, 
                  что каждый человек несет в себе уникальную мудрость, которой можно поделиться.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contact */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
            Связаться с нами
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white/80 backdrop-blur-sm border-emerald-100/50 text-center group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mb-4 mx-auto">
                  <Mail className="h-6 w-6 text-emerald-600" />
                </div>
                <CardTitle className="text-lg">Email</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">eunoia.community.eu@gmail.com</p>
              </CardContent>
            </Card>

            <a
              href="https://t.me/eunoiaspace_eu"
              target="_blank"
              rel="noopener noreferrer"
              className="block focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 rounded-lg"
              data-testid="about-telegram-link"
            >
              <Card className="bg-white/80 backdrop-blur-sm border-emerald-100/50 text-center group hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-100 rounded-full mb-4 mx-auto">
                    <Phone className="h-6 w-6 text-sky-600" />
                  </div>
                  <CardTitle className="text-lg">Telegram</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">@eunoiaspace_eu</p>
                </CardContent>
              </Card>
            </a>

            <Card className="bg-white/80 backdrop-blur-sm border-emerald-100/50 text-center group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-100 rounded-full mb-4 mx-auto">
                  <MapPin className="h-6 w-6 text-amber-600" />
                </div>
                <CardTitle className="text-lg">Офис</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Таллин, Эстония</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Join CTA — guests only */}
        {!user ? (
          <div id="join-community" ref={joinSectionRef} className="eunoia-surface p-12 text-center">
            <h2 className="eunoia-title-medium mb-6">
              Присоединяйтесь к нашему пути
            </h2>

            <p className="eunoia-body-large mb-8 max-w-2xl mx-auto">
              Независимо от того, новичок вы в духовных практиках или опытный практик,
              у нас найдется место для каждого на этом удивительном пути познания.
            </p>

            <a
              href={localizeTo("/login?redirect=/about%23join-community&intent=join_community")}
              data-testid="join-community-cta"
              className="eunoia-btn-primary inline-flex items-center gap-2"
              style={{ textDecoration: 'none' }}
            >
              Стать частью сообщества
              <Heart className="h-5 w-5" />
            </a>
          </div>
        ) : (
          // Already a member — soft, on-brand acknowledgement with a community-relevant CTA
          <div id="join-community" ref={joinSectionRef} className="eunoia-surface p-12 text-center">
            <h2 className="eunoia-title-medium mb-6">
              Вы уже часть сообщества
            </h2>

            <p className="eunoia-body-large mb-8 max-w-2xl mx-auto">
              Спасибо, что вы с нами. Познакомьтесь с организаторами и практиками,
              которые ведут пространство EUNOIA.
            </p>

            <Link
              to="/organizers"
              data-testid="explore-community-cta"
              className="eunoia-btn-primary inline-flex items-center gap-2"
              style={{ textDecoration: 'none' }}
            >
              Исследовать сообщество
              <Heart className="h-5 w-5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutPage;